import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatCurrency, formatDate, formatNumber } from "../lib/utils";

interface RecurringPayment {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  telegram_username: string | null;
  balance: number;
  is_enabled: boolean;
  mode: string;
  next_payment_date: string | null;
  monthly_amount: number | null;
  topup_amount: number | null;
  total_auto_topups: number;
  total_auto_topup_amount: number;
  card_last4: string | null;
  card_type: string | null;
}

export default function RecurringPaymentsPage() {
  const [statusFilter, setStatusFilter] = useState("enabled");
  const [modeFilter, setModeFilter] = useState("");

  const { data, isLoading } = useQuery<{ recurringPayments: RecurringPayment[] }>({
    queryKey: [
      `/api/admin/recurring-payments?status=${statusFilter}${modeFilter ? `&mode=${modeFilter}` : ""}`,
    ],
  });

  const payments = data?.recurringPayments || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Автоплатежи</h1>
        <span className="text-sm text-muted-foreground">
          Всего: {formatNumber(payments.length)}
        </span>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все</option>
          <option value="enabled">Активные</option>
          <option value="disabled">Отключённые</option>
        </select>
        <select
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все режимы</option>
          <option value="threshold">По порогу</option>
          <option value="monthly">Ежемесячно</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Пользователь
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Баланс
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Режим
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Сумма
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  След. платёж
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Карта
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Всего авто
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : payments.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Автоплатежи не найдены
                  </td>
                </tr>
              ) : (
                payments.map((rp) => (
                  <tr key={rp.user_id} className="hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <Link href={`/users/${rp.user_id}`}>
                        <span className="text-sm text-primary hover:underline cursor-pointer">
                          {rp.email}
                        </span>
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {[rp.first_name, rp.last_name]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">
                      {formatCurrency(Number(rp.balance))}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          rp.mode === "monthly"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {rp.mode}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">
                      {formatCurrency(
                        Number(rp.monthly_amount || rp.topup_amount || 0)
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {rp.next_payment_date
                        ? formatDate(rp.next_payment_date)
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {rp.card_last4
                        ? `${rp.card_type || ""} ****${rp.card_last4}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div>{rp.total_auto_topups} раз</div>
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(Number(rp.total_auto_topup_amount))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
